/**
 * Single owner of document.title + meta for the SPA.
 * Mount once under Router (App). Do not also set title in AccessibilityEnhancer.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  structuredData?: object;
  /** Force noindex regardless of path defaults */
  noIndex?: boolean;
}

interface PageSEOConfig {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: object;
  /** App/auth surfaces default to noindex */
  noIndex?: boolean;
}

const SITE = 'https://flahafast.flaha.org';
const BRAND = 'FlahaFAST';
const DEFAULT_OG = `${SITE}/assets/images/flahafast-og-image.png`;
/** Pattern: Page | FlahaFAST — brand always last for tab recognition */
const DEFAULT_TITLE = `${BRAND} | Hydroponic Nutrient Decision Support`;
const DEFAULT_DESC =
  'FlahaFAST is FlahaPA’s nutrient decision-support system. Calculate precise hydroponic solutions, manage water quality, and produce defensible crop formulations for professional agriculture.';

const titleOf = (page: string) => `${page} | ${BRAND}`;

/** Public / marketing — indexable */
const PUBLIC_SEO: Record<string, PageSEOConfig> = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESC,
    keywords:
      'hydroponics, nutrient calculator, hydroponic nutrients, plant nutrition, agriculture technology, precision farming, crop formulation, water quality analysis, stock solutions',
    ogImage: DEFAULT_OG,
    ogType: 'website',
  },
  '/support': {
    title: titleOf('Support & Documentation'),
    description:
      'Guides for the FlahaFAST nutrient DSS — pipeline, chemistry, water quality, and operations. FAQs and professional support resources.',
    keywords:
      'FlahaFAST support, hydroponics help, nutrient calculator support, fertigation troubleshooting',
    ogImage: DEFAULT_OG,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How do I calculate nutrient solutions with FlahaFAST?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'FlahaFAST uses a five-stage workflow: 1) Water quality, 2) Crop formulation, 3) Nutrient salts, 4) Recipe / stock solutions, 5) Results and export.',
          },
        },
        {
          '@type': 'Question',
          name: 'What makes FlahaFAST different from other hydroponic calculators?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'FlahaFAST is FlahaPA’s nutrient decision-support system with water quality analysis, crop targets, salt library, stock solution prep, and verification against scientific calculation standards.',
          },
        },
      ],
    },
  },
  '/support/video-tutorials': {
    title: titleOf('Video Tutorials'),
    description:
      'Walkthrough videos for FlahaFAST hydroponic nutrient workflows and DSS stages.',
  },
  '/support/agronomic-principles': {
    title: titleOf('Agronomic Principles'),
    description:
      'Nutrient uptake, pH, EC, and plant physiology basics for hydroponic operators using FlahaFAST.',
  },
  '/support/water-quality-management': {
    title: titleOf('Water Quality Management'),
    description:
      'Source water ions, testing, and how water quality enters the FlahaFAST recipe solve.',
  },
  '/support/advanced-calculations': {
    title: titleOf('Advanced Calculations'),
    description:
      'Matrix solve, units, degrees of freedom, concentration factor, and stock dilution explained.',
  },
  '/support/yield-optimization': {
    title: titleOf('Yield Optimization'),
    description:
      'Scheduling and operational practices around the nutrient recipe in FlahaFAST.',
  },
  '/login': {
    title: titleOf('Sign In'),
    description: 'Sign in to your FlahaFAST account for hydroponic nutrient workflows.',
    noIndex: true,
  },
  '/register': {
    title: titleOf('Create Account'),
    description: 'Register for FlahaFAST professional hydroponic nutrient management.',
    noIndex: true,
  },
  '/forgot-password': {
    title: titleOf('Forgot Password'),
    description: 'Request a password reset link for your FlahaFAST account.',
    noIndex: true,
  },
};

/** Authenticated app — always noindex */
const APP_SEO: Record<string, PageSEOConfig> = {
  '/dashboard': {
    title: titleOf('Workspace'),
    description: 'FlahaFAST workspace — resume your nutrient recipe pipeline.',
    noIndex: true,
  },
  '/water-quality': {
    title: titleOf('Water Quality'),
    description: 'Source water analysis for hydroponic nutrient calculations.',
    noIndex: true,
  },
  '/water-profile': {
    title: titleOf('Water Quality'),
    description: 'Source water analysis for hydroponic nutrient calculations.',
    noIndex: true,
  },
  '/formulation': {
    title: titleOf('Crop Formulation'),
    description: 'Crop nutrient targets and formulations.',
    noIndex: true,
  },
  '/formulations': {
    title: titleOf('Formulations'),
    description: 'Manage crop formulations.',
    noIndex: true,
  },
  '/salt-library': {
    title: titleOf('Salt Library'),
    description: 'Nutrient salt selection for your recipe.',
    noIndex: true,
  },
  '/stock-solutions': {
    title: titleOf('Recipe'),
    description: 'Stock solution parameters, solve, and review.',
    noIndex: true,
  },
  '/stock-solution': {
    title: titleOf('Recipe'),
    description: 'Stock solution parameters, solve, and review.',
    noIndex: true,
  },
  '/calculation': {
    title: titleOf('Calculator'),
    description: 'Unified calculation engine.',
    noIndex: true,
  },
  '/results': {
    title: titleOf('Results'),
    description: 'Recipe report and export.',
    noIndex: true,
  },
  '/profile': {
    title: titleOf('Profile'),
    description: 'Account profile and preferences.',
    noIndex: true,
  },
  '/admin': {
    title: titleOf('Admin'),
    description: 'Administration dashboard.',
    noIndex: true,
  },
  '/working-solution': {
    title: titleOf('Working Solution'),
    description: 'Working solution tools.',
    noIndex: true,
  },
  '/nutrient-deficiency': {
    title: titleOf('Nutrient Deficiency'),
    description: 'Deficiency calculator.',
    noIndex: true,
  },
  '/crop-recommendation': {
    title: titleOf('Crop Recommendation'),
    description: 'Crop recommendation tools.',
    noIndex: true,
  },
  '/nutrient-visualization': {
    title: titleOf('Visualization'),
    description: 'Nutrient visualization.',
    noIndex: true,
  },
  '/element-management': {
    title: titleOf('Element Management'),
    description: 'Elements and salts management.',
    noIndex: true,
  },
};

const SEO_CONFIG: Record<string, PageSEOConfig> = {
  ...PUBLIC_SEO,
  ...APP_SEO,
};

function resolveConfig(pathname: string): PageSEOConfig | null {
  if (SEO_CONFIG[pathname]) return SEO_CONFIG[pathname];
  // Support nested paths
  if (pathname.startsWith('/support/')) {
    return (
      SEO_CONFIG[pathname] || {
        title: titleOf('Support'),
        description: DEFAULT_DESC,
      }
    );
  }
  // Verify email tokens
  if (pathname.startsWith('/verify-email')) {
    return {
      title: titleOf('Verify Email'),
      description: 'Confirm your FlahaFAST account email address.',
      noIndex: true,
    };
  }
  if (pathname.startsWith('/reset-password')) {
    return {
      title: titleOf('Reset Password'),
      description: 'Set a new password for your FlahaFAST account.',
      noIndex: true,
    };
  }
  // Default: treat unknown as app (noindex)
  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESC,
    noIndex: true,
  };
}

const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage,
  ogType = 'website',
  canonicalUrl,
  structuredData,
  noIndex,
}) => {
  const location = useLocation();
  const pageConfig = resolveConfig(location.pathname) || {};

  const finalTitle = title || pageConfig.title || DEFAULT_TITLE;
  const finalDescription =
    description || pageConfig.description || DEFAULT_DESC;
  const finalKeywords =
    keywords ||
    pageConfig.keywords ||
    'hydroponics, nutrient calculator, precision agriculture';
  const finalOgImage = ogImage || pageConfig.ogImage || DEFAULT_OG;
  const finalOgType = ogType || pageConfig.ogType || 'website';
  const finalCanonicalUrl =
    canonicalUrl || `${SITE}${location.pathname === '/' ? '/' : location.pathname}`;
  const finalStructuredData = structuredData || pageConfig.structuredData;
  const finalNoIndex =
    noIndex === true || pageConfig.noIndex === true;

  useEffect(() => {
    document.title = finalTitle;

    updateMetaTag('description', finalDescription);
    updateMetaTag('title', finalTitle);
    updateMetaTag('keywords', finalKeywords);
    updateMetaTag(
      'robots',
      finalNoIndex ? 'noindex, nofollow' : 'index, follow'
    );

    updateMetaProperty('og:title', finalTitle);
    updateMetaProperty('og:site_name', BRAND);
    updateMetaProperty('og:description', finalDescription);
    updateMetaProperty('og:image', finalOgImage);
    updateMetaProperty('og:type', finalOgType);
    updateMetaProperty('og:url', finalCanonicalUrl);

    updateMetaProperty('twitter:title', finalTitle);
    updateMetaProperty('twitter:description', finalDescription);
    updateMetaProperty('twitter:image', finalOgImage);

    updateCanonicalUrl(finalCanonicalUrl);

    if (finalStructuredData) {
      updateStructuredData(finalStructuredData);
    } else {
      removePageStructuredData();
    }

    if (typeof gtag !== 'undefined') {
      gtag('config', 'G-YX5KE78HRV', {
        page_title: finalTitle,
        page_location: finalCanonicalUrl,
      });
    }
  }, [
    location.pathname,
    finalTitle,
    finalDescription,
    finalKeywords,
    finalOgImage,
    finalOgType,
    finalCanonicalUrl,
    finalStructuredData,
    finalNoIndex,
  ]);

  return null;
};

const updateMetaTag = (name: string, content: string) => {
  let element = document.querySelector(
    `meta[name="${name}"]`
  ) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
};

const updateMetaProperty = (property: string, content: string) => {
  let element = document.querySelector(
    `meta[property="${property}"]`
  ) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  element.content = content;
};

const updateCanonicalUrl = (url: string) => {
  let element = document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = url;
};

const updateStructuredData = (data: object) => {
  removePageStructuredData();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-page-structured-data', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

const removePageStructuredData = () => {
  document
    .querySelectorAll('script[data-page-structured-data]')
    .forEach((el) => el.remove());
};

export default SEOHead;
